import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { NormalizedV2Response } from '@/types/eds/api/responses/NormalizedV2Response';
import { UnlinkPolicyFromClaimRequest } from '@/domains/policies/types/api/requests/UnlinkPolicyBodyRequest';
import { ProducerEntityType } from '@/domains/producers/types/ProducerEntityType';
import { GetClaimResponse } from '../types/responses/GetClaimResponse';
import { ClaimSetClaimListSliceState } from '../types/types';
import { EdsEntityCodes } from '@/features/eds/enums/EdsEntityCodes';

const initialState: ClaimSetClaimListSliceState = {
  data: {
    listings: [],
    pagination: {
      numberOfItems: 0,
      first: '1',
      last: undefined,
      previous: '0',
      next: '0',
      page: 1,
      perPage: 20,
    },
  },
  dataRequestsStates: {
    claim: {
      id: null,
      isLoading: false,
      isFetching: false,
      isSuccess: false,
      isError: false,
    },
    claimListData: {
      isLoading: false,
      isFetching: false,
      isSuccess: false,
      isError: false,
    },
    benefitSectionListData: {},
  },
  policyData: {},
  coverageData: {},
  benefitData: {},
};

const claimSetClaimListSlice = createSlice({
  name: 'claimSetClaimList',
  initialState,
  reducers: {
    updateClaimSetClaim: (state, { payload }: PayloadAction<number | string>) => {
      state.dataRequestsStates.claim = {
        id: payload,
        isLoading: true,
        isFetching: true,
        isError: false,
        isSuccess: false,
      };
    },
    updateClaimSetClaimSuccess: (state, { payload }: PayloadAction<NormalizedV2Response>) => {
      const claimIndex = state.data.listings.findIndex((claim) => String(claim.id) === String(payload.id));

      if (claimIndex !== -1) state.data.listings[claimIndex] = payload;

      state.dataRequestsStates.claim = {
        id: state.dataRequestsStates.claim.id,
        isLoading: false,
        isFetching: false,
        isError: false,
        isSuccess: true,
      };
    },
    updateClaimSetClaimFailed: (state) => {
      state.dataRequestsStates.claim = {
        id: state.dataRequestsStates.claim.id,
        isLoading: false,
        isFetching: false,
        isError: true,
        isSuccess: false,
      };
    },
    getClaimSetClaimListData: (_, _action: PayloadAction<{ claimSetId: string }>) => {},
    startLoadingClaimSetClaimListData: (state) => {
      state.dataRequestsStates.claimListData = {
        isLoading: true,
        isFetching: !state.dataRequestsStates.claimListData,
        isError: false,
        isSuccess: false,
      };
    },
    startLoadingBenefitSectionDetails: (state, action: PayloadAction<{ claimId: string }>) => {
      const { claimId } = action.payload;

      state.dataRequestsStates.benefitSectionListData[claimId] = {
        isLoading: true,
        isFetching: !state.dataRequestsStates.benefitSectionListData[claimId],
        isError: false,
        isSuccess: false,
      };
    },
    getClaimSetClaimListDataSuccess: (
      state,
      { payload: { data, shouldRewrite = true } }: PayloadAction<{ data: GetClaimResponse; shouldRewrite?: boolean }>,
    ) => {
      const listingsArray = Array.isArray(data.listings) ? data.listings : [data.listings];

      if (shouldRewrite) {
        state.data.listings = listingsArray;
      } else {
        state.data.listings = [...state.data.listings, ...listingsArray];
      }

      state.data.pagination = data.pagination;

      state.dataRequestsStates.claimListData = {
        isLoading: false,
        isFetching: false,
        isError: false,
        isSuccess: true,
      };
    },
    getClaimSetClaimListDataError: (state) => {
      state.dataRequestsStates.claimListData = {
        isLoading: false,
        isFetching: false,
        isError: true,
        isSuccess: false,
      };
    },
    getBenefitSectionDetailsError: (state, action: PayloadAction<{ claimId: string }>) => {
      const { claimId } = action.payload;

      state.dataRequestsStates.benefitSectionListData[claimId] = {
        isLoading: false,
        isFetching: false,
        isError: true,
        isSuccess: false,
      };
    },
    incrementClaimSetClaimListPage: (state, _action: PayloadAction<{ claimSetId: string }>) => {
      state.data.pagination.page++;
    },
    resetClaimSetClaimListPage: () => initialState,
    getBenefitSectionDetails: (_, _action: PayloadAction<string>) => {},
    getBenefitSectionDetailsSuccess: (
      state,
      { payload }: PayloadAction<{ claimId: string; benefitSectionData: NormalizedV2Response }>,
    ) => {
      const { claimId, benefitSectionData } = payload;

      if (benefitSectionData.entities?.[EdsEntityCodes.ClaimCoverages]) {
        state.coverageData[benefitSectionData.id] = benefitSectionData.entities[
          EdsEntityCodes.ClaimCoverages
        ] as NormalizedV2Response[];
      } else {
        delete state.coverageData[benefitSectionData.id];
      }

      if (benefitSectionData.entities?.[EdsEntityCodes.Policy]) {
        state.policyData[benefitSectionData.id] = benefitSectionData.entities[
          EdsEntityCodes.Policy
        ] as NormalizedV2Response;
      } else {
        delete state.policyData[benefitSectionData.id];
      }

      if (benefitSectionData.entities?.[EdsEntityCodes.Benefit]) {
        state.benefitData[benefitSectionData.id] = benefitSectionData.entities[
          EdsEntityCodes.Benefit
        ] as NormalizedV2Response;
      } else {
        delete state.benefitData[benefitSectionData.id];
      }

      state.dataRequestsStates.benefitSectionListData[claimId] = {
        isLoading: false,
        isFetching: false,
        isError: false,
        isSuccess: true,
      };
    },
    unlinkPolicyFromClaim: (_, _action: PayloadAction<UnlinkPolicyFromClaimRequest>) => {},
    deleteClaimSetClaim: (state, _action: PayloadAction<number | string>) => {
      state.dataRequestsStates.claimListData = {
        isLoading: true,
        isFetching: !state.dataRequestsStates.claimListData,
        isError: false,
        isSuccess: false,
      };
    },
    deleteBenefitSectionDetails: (_, _action: PayloadAction<{ claimId: string; benefitId: string }>) => {},
    deleteClaimSetClaimError: (state) => {
      state.dataRequestsStates.claimListData = {
        ...initialState.dataRequestsStates.claimListData,
        isError: true,
      };
    },
    unlinkProducerFromClaim: (_, _action: PayloadAction<{ claimId: string; unlinkLevel: ProducerEntityType }>) => {},
  },
});

export const {
  updateClaimSetClaim,
  updateClaimSetClaimSuccess,
  updateClaimSetClaimFailed,
  getClaimSetClaimListData,
  startLoadingClaimSetClaimListData,
  startLoadingBenefitSectionDetails,
  getClaimSetClaimListDataSuccess,
  getClaimSetClaimListDataError,
  getBenefitSectionDetailsError,
  incrementClaimSetClaimListPage,
  resetClaimSetClaimListPage,
  getBenefitSectionDetails,
  getBenefitSectionDetailsSuccess,
  unlinkPolicyFromClaim,
  deleteClaimSetClaim,
  deleteClaimSetClaimError,
  deleteBenefitSectionDetails,
  unlinkProducerFromClaim,
} = claimSetClaimListSlice.actions;

export const claimSetClaimListReducer = claimSetClaimListSlice.reducer;
export const claimSetClaimListReducerPath = claimSetClaimListSlice.reducerPath;
